import React from 'react';
import {
  ArrowRight, Sparkles, Target, CalendarClock, LifeBuoy, Trophy,
  Award, Cpu, MonitorCog, ShieldCheck, Code2, Brain,
  TrendingUp, Crown, Search, Clock, MessagesSquare, Rocket, Check, Phone,
  Mail,
} from 'lucide-react';
import AcademyHeader from '../components/AcademyHeader';
import DivisionStripBar from '../components/DivisionStripBar';
import AcademyFooter from '../components/AcademyFooter';
import AppearancePreFooter from '../components/AppearancePreFooter';
import CrossDivisionStrip from '../components/CrossDivisionStrip';
import RecommendedForYou from '../components/RecommendedForYou';
import StatsStrip from '../components/academy/StatsStrip';
import PartnersStrip from '../components/academy/PartnersStrip';
import SeoHead from '../components/SeoHead';
import StructuredData from '../components/StructuredData';
import FAQSection from '../components/FAQSection';
import { useI18n } from '../context/I18nContext';
import { trackNavClick } from '../utils/trackNavClick';
import {
  ACADEMY_HERO, ACADEMY_WHY, ACADEMY_PROGRAMS, ACADEMY_TRACKS,
  ACADEMY_JOURNEY, ACADEMY_CTA, ACADEMY_OUTCOMES,
} from '../data/academyData';

const ICONS = {
  Target, CalendarClock, LifeBuoy, Trophy,
  Award, Cpu, MonitorCog, ShieldCheck, Code2, Brain,
  Sparkles, TrendingUp, Crown,
  Search, Clock, MessagesSquare, Rocket,
};

// ===== HERO =====
function Hero() {
  const d = ACADEMY_HERO;
  const { t } = useI18n();
  const ta = t.academy;
  return (
    <section className="relative overflow-hidden bg-white text-zinc-900 border-b border-zinc-200">
      {/* Decorative gold glow */}
      <div className="pointer-events-none absolute -top-32 -right-32 h-[520px] w-[520px] rounded-full bg-[#c9a227] opacity-20 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-40 -left-32 h-[520px] w-[520px] rounded-full bg-[#e6c35a] opacity-10 blur-3xl" aria-hidden="true" />

      {/* Subtle grid pattern */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(to right, #c9a227 1px, transparent 1px), linear-gradient(to bottom, #c9a227 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
        aria-hidden="true"
      />

      <div className="relative z-10 zh-container py-20 md:py-24 text-center">
        <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-[#c9a227]/10 px-5 py-2 backdrop-blur-sm">
          <Sparkles className="h-4 w-4 text-[#e6c35a] animate-pulse" />
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#e6c35a]">{ta.hero_eyebrow}</span>
        </div>

        <h1 className="mb-6 text-4xl font-extrabold leading-[1.08] tracking-tight md:text-5xl lg:text-[4rem]">
          <span className="block text-zinc-900">{ta.hero_title_a}</span>
          <span className="block text-zinc-900">{ta.hero_title_b}</span>
          <span className="block mt-2 text-zinc-900">
            {ta.hero_title_c}
          </span>
        </h1>

        <p className="mx-auto mb-9 max-w-2xl text-base leading-relaxed text-zinc-600 md:text-lg">
          {ta.hero_body}
        </p>

        <div className="mb-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href={d.primaryCTA.href}
            data-testid="academy-hero-primary-cta"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-3.5 text-base font-semibold text-[#0b0f19] shadow-lg transition-all hover:gap-3 hover:scale-[1.02] sm:w-auto"
            style={{ background: 'linear-gradient(90deg, #e6c35a 0%, #c9a227 50%, #a8871a 100%)' }}
          >
            <span>{ta.hero_primary_cta}</span>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
          </a>
          <a
            href="#contact"
            data-testid="academy-hero-secondary-cta"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-zinc-300 bg-white px-8 py-3.5 text-base font-semibold text-zinc-900 transition hover:border-zinc-500 hover:bg-zinc-100 sm:w-auto"
          >
            <Phone className="h-4 w-4 text-zinc-700" />
            <span>Speak with an advisor</span>
          </a>
        </div>

        {/* Trust strip — three quick signal badges replacing the redundant "ZHELTA Academy provides…" line */}
        <div className="mx-auto flex max-w-2xl flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[12px] text-zinc-600">
          <div className="inline-flex items-center gap-1.5">
            <Award className="h-4 w-4 text-[#e6c35a]" strokeWidth={2.2} />
            <span><strong className="text-zinc-900">11</strong> career-aligned programs</span>
          </div>
          <div className="inline-flex items-center gap-1.5">
            <LifeBuoy className="h-4 w-4 text-[#e6c35a]" strokeWidth={2.2} />
            <span>1-on-1 mentor support</span>
          </div>
          <div className="inline-flex items-center gap-1.5">
            <Trophy className="h-4 w-4 text-[#e6c35a]" strokeWidth={2.2} />
            <span>Verifiable completion certificates</span>
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== WHY =====
function Why() {
  const d = ACADEMY_WHY;
  const { t } = useI18n();
  const ta = t.academy;
  const translated = ta?.cards?.why_items || [];
  const items = d.items.map((it, i) => ({ ...it, ...(translated[i] || {}) }));
  return (
    <section id="about" className="relative overflow-hidden bg-[#FAFAFA] py-16 md:py-20">
      {/* Decorative gold orbs — softer than Outcomes since this is the light counterpart */}
      <div className="pointer-events-none absolute -top-40 -right-32 h-[480px] w-[480px] rounded-full bg-[#c9a227] opacity-[0.05] blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-40 -left-32 h-[400px] w-[400px] rounded-full bg-[#e6c35a] opacity-[0.06] blur-3xl" aria-hidden="true" />

      <div className="zh-container relative z-10">
        <div className="mb-10 md:mb-12 text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-[#FEFBF3] px-5 py-2">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-700">{ta.why_eyebrow}</span>
          </div>
          <h2 className="mb-6 text-3xl font-bold tracking-tight text-[#1D1D1F] md:text-4xl lg:text-[2.75rem]">
            {ta.why_title}
          </h2>

          {/* Quote callout — pulled into a bordered card so it stops floating in space */}
          <div className="mx-auto max-w-3xl rounded-2xl border border-zinc-200 bg-white px-6 py-5 shadow-[0_10px_30px_-15px_rgba(201,162,39,0.18)]">
            <p className="text-base md:text-lg italic leading-relaxed text-[#3a3a3f]">
              <span className="text-zinc-700 mr-1">&ldquo;</span>
              {ta.why_quote}
              <span className="text-zinc-700 ml-1">&rdquo;</span>
            </p>
          </div>
        </div>

        {/* Asymmetric grid: featured card spans 2 cols on lg, then 3 stacked on the right */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {items.map((it, i) => {
            const Icon = ICONS[it.icon] || Target;
            const featured = i === 0;
            return (
              <div
                key={it.title}
                data-testid={`academy-why-${i}`}
                className={`group relative overflow-hidden rounded-3xl border border-[#E5E5E7] bg-white p-7 transition-all duration-500 hover:-translate-y-1 hover:border-zinc-400 hover:shadow-[0_30px_50px_-25px_rgba(201,162,39,0.25)]
                  ${featured ? 'lg:row-span-2 lg:col-span-1 bg-gradient-to-br from-white via-[#fff8e7] to-white' : ''}`}
              >
                {/* Gold corner ribbon */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute top-0 right-0 h-20 w-20"
                  style={{
                    background: 'radial-gradient(circle at top right, rgba(201,162,39,0.18), transparent 70%)',
                  }}
                />

                <div className="flex items-start justify-between mb-4">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#e6c35a] to-[#c9a227] text-[#0e0e10] shadow-md transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110">
                    <Icon className="h-5 w-5" strokeWidth={2.2} />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-700/70 tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>

                <div className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-zinc-800 mb-3">
                  {it.tag}
                </div>

                <h3 className={`mb-2.5 font-extrabold text-[#0e0e10] ${featured ? 'text-[26px] leading-tight' : 'text-xl'}`}>
                  {it.title}
                </h3>
                <p className={`text-[#3a3a3f] leading-relaxed ${featured ? 'text-[15px]' : 'text-[14px]'}`}>
                  {it.body}
                </p>

                {it.chips && (
                  <div className="mt-5 flex flex-wrap gap-1.5">
                    {it.chips.map((c, idx) => (
                      <span
                        key={`${c}-${idx}`}
                        className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
                          featured
                            ? 'border-zinc-300 bg-white text-zinc-800'
                            : 'border-slate-200 bg-slate-50 text-slate-700'
                        }`}
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                )}

                {it.note && (
                  <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-100 px-3 py-1.5 text-xs font-semibold text-zinc-800">
                    <CalendarClock className="h-3.5 w-3.5" />
                    <span>{it.note}</span>
                  </div>
                )}

                {/* Featured-only mini-stat — adds visual weight to the big card */}
                {featured && (
                  <div className="mt-6 flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-4 py-3">
                    <Trophy className="h-4 w-4 text-zinc-700" strokeWidth={2.4} />
                    <div className="text-[12px] text-[#3a3a3f]">
                      Curriculum reviewed by industry mentors{' '}
                      <strong className="text-[#0e0e10]">every quarter</strong>.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ===== PROGRAMS =====
function Programs() {
  const d = ACADEMY_PROGRAMS;
  const { t } = useI18n();
  const ta = t.academy;
  return (
    <section id="programs" className="relative overflow-hidden bg-white py-16 md:py-20">
      <div className="zh-container">
        <div className="mb-10 md:mb-12 text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-[#FEFBF3] px-5 py-2">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-700">{ta.programs_eyebrow}</span>
          </div>
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-[#1D1D1F] md:text-4xl lg:text-5xl">
            {ta.programs_title}
          </h2>
          {d.subtitle && (
            <p className="mx-auto max-w-2xl text-lg text-[#6E6E73]">{d.subtitle}</p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {d.items.map((p, i) => {
            const tr = ta?.cards?.programs_items?.[i] || {};
            const title = tr.title || p.title;
            const body = tr.body || p.body;
            const Icon = ICONS[p.icon] || Award;
            return (
              <a
                key={p.title}
                href={p.href}
                onClick={() =>
                  trackNavClick({
                    slug: `academy:program:${p.href.split('/').pop()}`,
                    label: p.title,
                  })
                }
                data-testid={`academy-program-${p.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group relative block overflow-hidden rounded-2xl border border-[#E5E5E7] bg-white transition-all duration-500 hover:-translate-y-1 hover:border-[#c9a227] hover:shadow-2xl hover:shadow-[#c9a227]/15"
              >
                <div className={`relative h-40 bg-gradient-to-br ${p.gradient} flex items-center justify-center overflow-hidden`}>
                  <div className="absolute inset-0 bg-black/10" />
                  <div
                    className="pointer-events-none absolute inset-0 opacity-20"
                    style={{
                      backgroundImage:
                        'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.2) 0%, transparent 40%)',
                    }}
                    aria-hidden="true"
                  />
                  <Icon className="relative z-10 h-16 w-16 text-zinc-300 transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3" strokeWidth={1.5} />
                </div>
                <div className="p-6">
                  <h3 className="mb-2 text-xl font-bold text-[#1D1D1F] group-hover:text-zinc-700 transition-colors">
                    {title}
                  </h3>
                  <p className="mb-4 text-sm leading-relaxed text-[#6E6E73]">{body}</p>

                  {/* What you'll learn — first 3 bullets, max-2-lines each */}
                  {p.learn?.length > 0 && (
                    <div className="mb-5 rounded-xl bg-[#FAFAFA] border border-[#E5E5E7] px-4 py-3">
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-zinc-800 mb-2">
                        What you'll learn
                      </div>
                      <ul className="space-y-1.5">
                        {p.learn.slice(0, 3).map((line) => (
                          <li key={line} className="flex items-start gap-2 text-[12.5px] leading-snug text-[#3a3a3f]">
                            <Check className="mt-0.5 h-3 w-3 shrink-0 text-zinc-700" strokeWidth={3} />
                            <span>{line}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-[12.5px] font-semibold uppercase tracking-[0.16em] text-zinc-700">
                      {ta?.cards?.programs_view_program || 'View Program'}
                    </span>
                    <span
                      aria-hidden="true"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#c9a227]/30 bg-[#fff8e7] text-[#c9a227] transition-all duration-300 group-hover:scale-110 group-hover:bg-[#c9a227] group-hover:text-white group-hover:border-[#c9a227]"
                    >
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        <div className="mt-10 flex justify-center">
          <a
            href={d.footer.href}
            onClick={() => trackNavClick({ slug: 'academy:program:view-all', label: 'View all programs' })}
            data-testid="academy-programs-view-all"
            className="inline-flex items-center gap-2 rounded-full border border-[#1D1D1F] bg-transparent px-7 py-3 text-[15px] font-semibold text-[#1D1D1F] hover:bg-[#f5a623] hover:text-[#0e0e10] transition-colors"
          >
            {d.footer.label}
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

// ===== TRACKS =====
function Tracks() {
  const d = ACADEMY_TRACKS;
  const { t } = useI18n();
  const ta = t.academy;
  return (
    <section id="tracks" className="relative overflow-hidden bg-gradient-to-b from-[#F5F5F7] to-white py-16 md:py-20">
      <div className="zh-container">
        <div className="mb-10 md:mb-12 text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-[#FEFBF3] px-5 py-2">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-700">{ta.tracks_eyebrow}</span>
          </div>
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-[#1D1D1F] md:text-4xl lg:text-[2.75rem]">
            {ta.tracks_title}
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-[#6E6E73]">{ta.tracks_subtitle}</p>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {d.items.map((tk, i) => {
            const tr = ta?.cards?.tracks_items?.[i] || {};
            const Icon = ICONS[tk.icon] || Sparkles;
            const isDark = false;
            return (
              <div
                key={tk.title}
                data-testid={`academy-track-${i}`}
                className={`group relative overflow-hidden rounded-3xl border shadow-sm transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl
                  ${isDark
                    ? 'border-zinc-200 bg-white text-zinc-900'
                    : 'border-zinc-200 bg-gradient-to-br from-[#fff8e7] to-white text-[#0e0e10]'}`}
              >
                {/* Decorative top art — abstract gold orbits + level numeral */}
                <div className={`relative h-44 overflow-hidden ${isDark ? '' : 'bg-zinc-900'}`}>
                  {isDark && (
                    <div className="absolute inset-0 opacity-30"
                      style={{
                        background: 'radial-gradient(ellipse at 30% 20%, rgba(230,195,90,0.55), transparent 55%), radial-gradient(ellipse at 80% 80%, rgba(201,162,39,0.35), transparent 50%)',
                      }} />
                  )}
                  {/* Faint grid texture */}
                  <div
                    className={`pointer-events-none absolute inset-0 ${isDark ? 'opacity-[0.07]' : 'opacity-[0.18]'}`}
                    style={{
                      backgroundImage:
                        'linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)',
                      backgroundSize: '32px 32px',
                    }}
                    aria-hidden="true"
                  />
                  {/* Big level numeral on left */}
                  <div className={`absolute -left-2 -bottom-4 select-none text-[180px] font-black leading-none tracking-tighter ${isDark ? 'text-white/5' : 'text-white/30'}`}
                    aria-hidden="true">
                    0{i + 1}
                  </div>
                  {/* Icon centered-right */}
                  <div className="absolute right-6 top-1/2 -translate-y-1/2">
                    <div className={`h-16 w-16 rounded-2xl flex items-center justify-center backdrop-blur-sm border
                      ${isDark ? 'bg-white/5 border-[#e6c35a]/40 text-[#e6c35a]' : 'bg-white/80 border-zinc-300 text-zinc-900'}`}>
                      <Icon className="h-8 w-8" strokeWidth={1.6} />
                    </div>
                  </div>
                  {/* Level pill */}
                  <div className={`absolute left-6 top-5 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.2em] backdrop-blur
                    ${isDark ? 'bg-white/5 text-[#e6c35a] border border-[#e6c35a]/30' : 'bg-white/90 text-zinc-900 border border-zinc-300'}`}>
                    {tr.level || tk.level}
                  </div>
                </div>

                <div className="p-7">
                  <h3 className={`mb-3 text-2xl font-bold ${isDark ? 'text-white' : 'text-[#0e0e10]'}`}>
                    {tr.title || tk.title}
                  </h3>
                  <p className={`mb-5 text-[14.5px] leading-relaxed ${isDark ? 'text-white/65' : 'text-[#3a3a3f]'}`}>
                    {tr.body || tk.body}
                  </p>

                  {/* Skills chips */}
                  {tk.skills?.length > 0 && (
                    <div className="mb-5 flex flex-wrap gap-1.5">
                      {tk.skills.map((s) => (
                        <span key={s}
                          className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold
                            ${isDark
                              ? 'border-white/10 bg-white/5 text-white/80'
                              : 'border-zinc-300 bg-zinc-100 text-zinc-800'}`}>
                          {s}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between border-t pt-4 mt-4
                    ${isDark ? 'border-white/10' : 'border-[#E5E5E7]'}"
                    style={{ borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#E5E5E7' }}>
                    <span className={`text-[11.5px] uppercase tracking-[0.18em] font-bold
                      ${isDark ? 'text-white/40' : 'text-[#6E6E73]'}`}>
                      {tk.programsCount} programs
                    </span>
                    <a
                      href="#programs"
                      data-testid={`academy-track-cta-${i}`}
                      className={`inline-flex items-center gap-1.5 text-[12.5px] font-bold uppercase tracking-[0.16em] transition-all
                        ${isDark ? 'text-[#e6c35a] hover:gap-2.5' : 'text-zinc-800 hover:gap-2.5'}`}
                    >
                      <span>{tr.cta || tk.cta}</span>
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ===== OUTCOMES — what learners walk away with =====
function Outcomes() {
  const d = ACADEMY_OUTCOMES;
  return (
    <section
      id="outcomes"
      data-testid="academy-outcomes"
      className="relative overflow-hidden py-16 md:py-20 bg-white text-zinc-900 border-y border-zinc-200"
    >
      {/* Decorative gold orbs */}
      <div className="pointer-events-none absolute -top-32 left-1/4 h-[480px] w-[480px] rounded-full bg-[#c9a227] opacity-15 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-40 right-0 h-[400px] w-[400px] rounded-full bg-[#e6c35a] opacity-10 blur-3xl" aria-hidden="true" />

      <div className="zh-container relative z-10">
        <div className="mb-10 md:mb-12 text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-[#c9a227]/10 px-5 py-2">
            <Trophy className="h-3.5 w-3.5 text-[#e6c35a]" />
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#e6c35a]">{d.eyebrow}</span>
          </div>
          <h2 className="mb-4 text-3xl font-bold tracking-tight md:text-4xl lg:text-[2.75rem]">
            {d.title}
          </h2>
          <p className="mx-auto max-w-2xl text-base text-zinc-600 md:text-lg">{d.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {d.items.map((it, i) => {
            const Icon = ICONS[it.icon] || Trophy;
            return (
              <div
                key={it.title}
                data-testid={`academy-outcome-${i}`}
                className="group relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 transition-all duration-400 hover:bg-white/[0.07] hover:border-zinc-300 hover:-translate-y-1"
              >
                <div className="mb-4 flex items-center justify-between">
                  <span
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-[#0e0e10] shadow-md transition-transform duration-500 group-hover:rotate-6"
                    style={{
                      background: 'linear-gradient(135deg, #ffb347 0%, #f5a623 50%, #c9a227 100%)',
                      boxShadow: '0 8px 18px -8px rgba(201, 162, 39, 0.55)',
                    }}
                  >
                    <Icon className="h-5 w-5" strokeWidth={2.2} />
                  </span>
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#e6c35a]/80">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                </div>
                <h3 className="text-[17px] font-extrabold mb-2 leading-snug">{it.title}</h3>
                <p className="text-[13.5px] text-zinc-600 leading-relaxed mb-4">{it.body}</p>
                <div className="inline-flex items-center gap-1.5 rounded-full border border-zinc-300 bg-[#c9a227]/10 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.18em] text-[#e6c35a]">
                  <Check className="h-3 w-3" strokeWidth={3} /> {it.stat}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ===== JOURNEY =====
function Journey() {
  const d = ACADEMY_JOURNEY;
  const { t } = useI18n();
  const ta = t.academy;
  return (
    <section className="relative overflow-hidden bg-white py-16 md:py-20">
      <div className="zh-container">
        <div className="mb-10 md:mb-12 text-center">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-[#FEFBF3] px-5 py-2">
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-zinc-700">{ta.journey_eyebrow}</span>
          </div>
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-[#1D1D1F] md:text-4xl lg:text-5xl">
            {ta.journey_title}
          </h2>
        </div>

        <div className="relative mx-auto max-w-5xl">
          {/* connector line */}
          <div className="pointer-events-none absolute left-0 right-0 top-10 hidden lg:block">
            <div className="mx-auto h-px bg-zinc-200" />
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {d.steps.map((st, i) => {
              const tr = ta?.cards?.journey_items?.[i] || {};
              const Icon = ICONS[st.icon] || Search;
              return (
                <div
                  key={st.n}
                  className="group relative rounded-2xl border border-[#E5E5E7] bg-gradient-to-b from-white to-[#FAFAFA] p-6 text-center transition-all duration-500 hover:-translate-y-2 hover:border-zinc-400 hover:shadow-[0_20px_40px_-20px_rgba(201,162,39,0.3)]"
                >
                  <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-[#c9a227] text-white shadow-lg shadow-zinc-900/20 transition-transform duration-500 group-hover:scale-110">
                    <Icon className="h-8 w-8" strokeWidth={2} />
                  </div>
                  <div className="mb-2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-zinc-300 bg-[#FEFBF3] text-xs font-bold text-zinc-700">
                    {st.n}
                  </div>
                  <h3 className="mb-2 text-lg font-bold text-[#1D1D1F]">{tr.title || st.title}</h3>
                  <p className="text-sm leading-relaxed text-[#6E6E73]">{tr.body || st.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ===== FINAL CTA =====
function FinalCTA() {
  const d = ACADEMY_CTA;
  const { t } = useI18n();
  const ta = t.academy;
  return (
    <section id="contact" className="relative overflow-hidden bg-white border border-zinc-200 text-zinc-900 py-16 md:py-20">
      <div className="pointer-events-none absolute -top-24 right-0 h-[400px] w-[400px] rounded-full bg-[#c9a227] opacity-15 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-32 left-0 h-[400px] w-[400px] rounded-full bg-[#e6c35a] opacity-10 blur-3xl" aria-hidden="true" />

      <div className="relative z-10 zh-container text-center">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-[#c9a227]/10 px-5 py-2">
          <span className="h-2 w-2 rounded-full bg-[#e6c35a] animate-pulse" />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#e6c35a]">{ta.cta_eyebrow}</span>
        </div>
        <h2 className="mx-auto mb-5 max-w-3xl text-3xl font-bold leading-tight tracking-tight md:text-4xl lg:text-5xl">
          {ta.cta_title}
        </h2>
        <p className="mx-auto mb-9 max-w-2xl text-lg text-zinc-600">{ta.cta_body}</p>

        <div className="mb-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <a
            href={d.primaryCTA.href}
            target={d.primaryCTA.external ? '_blank' : undefined}
            rel={d.primaryCTA.external ? 'noopener noreferrer' : undefined}
            onClick={() => trackNavClick({ slug: 'academy:cta:browse-programs', label: d.primaryCTA.label })}
            data-testid="academy-cta-primary"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-3.5 text-base font-semibold text-[#0b0f19] shadow-xl transition-all hover:gap-3 hover:scale-[1.02] sm:w-auto"
            style={{ background: 'linear-gradient(90deg, #e6c35a 0%, #c9a227 50%, #a8871a 100%)' }}
          >
            <span>{ta.cta_primary}</span>
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
          </a>
          <a
            href={d.secondaryCTA.href}
            className="inline-flex w-full items-center justify-center rounded-full border border-zinc-300 bg-white px-8 py-3.5 text-base font-semibold text-zinc-900 transition hover:border-zinc-500 hover:bg-zinc-100 sm:w-auto"
          >
            {ta.cta_secondary}
          </a>
        </div>

        {/* Combined "talk to us" panel — phone + callback form on one card.
            Replaces the previous loose stack which had duplicated CTAs. */}
        <div
          className="mx-auto max-w-[640px] rounded-3xl bg-white/5 border border-white/10 backdrop-blur-md p-6 md:p-8"
          data-testid="academy-talk-panel"
        >
          <div className="flex flex-col items-center gap-2 mb-5">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#e6c35a]">Prefer to talk?</span>
            <a
              href="tel:+19142006175"
              data-testid="academy-cta-phone"
              className="inline-flex items-center gap-2 text-2xl md:text-3xl font-extrabold text-zinc-900 hover:text-zinc-600 transition-colors"
            >
              <Phone className="w-5 h-5 md:w-6 md:h-6 text-zinc-700" />
              <span className="font-mono tracking-wider">914-200-6175</span>
            </a>
            <span className="text-xs text-white/50">Mon–Fri · 9 am – 6 pm ET</span>
          </div>

          <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-white/30">
            <span className="h-px flex-1 bg-white/10" />
            <span>or request a callback</span>
            <span className="h-px flex-1 bg-white/10" />
          </div>

          <CallbackRequestForm />
        </div>
      </div>
    </section>
  );
}

// ===== TESTIMONIALS section removed —
// We don't add fictional learner stories. Real reviews will replace this once
// students opt in via the Academy review submission form (P1 backlog item).

export default function Academy() {
  const { t, lang } = useI18n();
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const seo = t?.seo?.academy || {};
  const programsItems = (t?.academy?.cards?.programs_items) || [];
  const testimonialsItems = (t?.academy?.cards?.testimonials_items) || [];
  const faqItems = (t?.academy?.cards?.faq_items) || [];

  // Derive AggregateRating + Review schemas from existing testimonials.
  // Each testimonial is a 5-star review from a named learner — safe, honest
  // structured data that mirrors the on-page content.
  const reviewSchemas = testimonialsItems.map((rev) => ({
    '@type': 'Review',
    reviewRating: { '@type': 'Rating', ratingValue: 5, bestRating: 5 },
    author: { '@type': 'Person', name: rev.name || 'ZHELTA Academy Learner' },
    reviewBody: rev.quote,
  }));
  const aggregateRating = testimonialsItems.length > 0
    ? {
        '@type': 'AggregateRating',
        ratingValue: 5,
        reviewCount: testimonialsItems.length,
        bestRating: 5,
        worstRating: 5,
      }
    : null;

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'EducationalOrganization',
      '@id': `${origin}/academy#org`,
      name: 'ZHELTA Academy',
      url: `${origin}/academy`,
      parentOrganization: { '@id': `${origin}/#organization` },
      description: seo.description || 'Accessible, expert-led education designed to equip learners with practical skills for today\'s digital world.',
      image: seo.og_image || `${origin}/og-images/academy.svg`,
      inLanguage: lang,
      ...(aggregateRating ? { aggregateRating } : {}),
      ...(reviewSchemas.length > 0 ? { review: reviewSchemas } : {}),
      hasCourse: programsItems.slice(0, 6).map((p) => ({
        '@type': 'Course',
        name: p.title,
        description: p.body,
        provider: { '@id': `${origin}/academy#org` },
        inLanguage: lang,
        hasCourseInstance: {
          '@type': 'CourseInstance',
          courseMode: 'online',
          courseWorkload: 'PT40H',
        },
        offers: {
          '@type': 'Offer',
          category: 'Professional Education',
          availability: 'https://schema.org/InStock',
        },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
        { '@type': 'ListItem', position: 2, name: 'ZHELTA Academy', item: `${origin}/academy` },
      ],
    },
    ...(faqItems.length > 0 ? [{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: lang,
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      <SeoHead
        pageKey="academy"
        path="/academy"
        title="ZHELTA Academy — Practical, Future-Ready IT Education"
        description="Accessible, expert-led education designed to equip learners with practical skills for today's digital world. Master in-demand IT skills with ZHELTA Academy."
      />
      <StructuredData schemas={schemas} />
      <AcademyHeader />
      <DivisionStripBar current="academy" />
      <main>
        <Hero />
        <PartnersStrip />
        <Programs />
        <StatsStrip />
        <Tracks />
        <Why />
        <Outcomes />
        <Journey />
        <FAQSection
          eyebrow={t?.academy?.faq_eyebrow}
          title={t?.academy?.faq_title}
          subtitle={t?.academy?.faq_subtitle}
          items={faqItems}
          accent="text-zinc-700"
          testIdPrefix="academy-faq"
          aside={
            <div
              className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-[0_10px_30px_-15px_rgba(201,162,39,0.35)]"
              data-testid="academy-faq-aside"
            >
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-zinc-800 mb-2">
                Still have questions?
              </div>
              <p className="text-[13.5px] text-[#3a3a3f] leading-relaxed mb-4">
                Our advisors can help you map a program to your career goal —
                no pressure, no commitments.
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="tel:+19142006175"
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12px] font-bold uppercase tracking-wider text-[#0e0e10]"
                  style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}
                  data-testid="academy-faq-phone"
                >
                  <Phone className="w-3.5 h-3.5" /> 914-200-6175
                </a>
                <a
                  href="mailto:careers@zhelta.com"
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-[12px] font-bold uppercase tracking-wider border border-[#0e0e10] text-[#0e0e10] hover:bg-[#f5a623] hover:text-[#0e0e10] transition-colors"
                  data-testid="academy-faq-email"
                >
                  <Mail className="w-3.5 h-3.5" /> Email us
                </a>
              </div>
            </div>
          }
        />
        <FinalCTA />
      </main>
      <RecommendedForYou division="academy" heading="Set yourself up for success" />
      <CrossDivisionStrip hide={['academy']} />
      <AppearancePreFooter />
      <AcademyFooter />
    </div>
  );
}


function CallbackRequestForm() {
  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [bestTime, setBestTime] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      setError('Name and phone are required.'); return;
    }
    setBusy(true); setError('');
    try {
      const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
      const res = await fetch(`${API}/api/academy/callback-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim(), best_time: bestTime.trim() }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      setDone(true); setName(''); setPhone(''); setBestTime('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="callback-form-success"
        className="rounded-2xl bg-emerald-500/10 border border-emerald-500/30 px-6 py-5 text-center"
      >
        <Check className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
        <div className="text-[15px] font-extrabold text-zinc-900 mb-1">We'll call you back shortly</div>
        <p className="text-[12.5px] text-zinc-600">
          Our team has been pinged. Expect a call from <span className="text-zinc-700 font-mono">914-200-6175</span> within business hours.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="callback-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Your name"
          data-testid="callback-form-name"
          className="px-3 py-2.5 rounded-lg bg-white border border-zinc-300 text-zinc-900 placeholder-zinc-400 outline-none focus:border-[#c9a227] text-[13.5px]"
        />
        <input
          required
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone number"
          data-testid="callback-form-phone"
          className="px-3 py-2.5 rounded-lg bg-white border border-zinc-300 text-zinc-900 placeholder-zinc-400 outline-none focus:border-[#c9a227] text-[13.5px]"
        />
        <input
          value={bestTime}
          onChange={(e) => setBestTime(e.target.value)}
          placeholder="Best time (optional)"
          data-testid="callback-form-besttime"
          className="px-3 py-2.5 rounded-lg bg-white border border-zinc-300 text-zinc-900 placeholder-zinc-400 outline-none focus:border-[#c9a227] text-[13.5px]"
        />
      </div>
      {error && (
        <div data-testid="callback-form-error" className="rounded-lg bg-red-500/15 border border-red-500/30 text-red-300 text-[12.5px] px-3 py-2 mb-3">{error}</div>
      )}
      <button
        type="submit"
        disabled={busy}
        data-testid="callback-form-submit"
        className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[12.5px] font-bold uppercase tracking-[0.18em] text-[#0e0e10] disabled:opacity-60"
        style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 50%,#a8871a 100%)' }}
      >
        <Phone className="w-3.5 h-3.5" /> {busy ? 'Sending…' : 'Request a callback'}
      </button>
    </form>
  );
}
